import { Material, MaterialWithFileMetadata, OutlineNode } from '../types';
import { parseDocxOutline } from '../utils/docxUtils';
import { getFileType } from '../utils/file';
import API from './api';

export interface MaterialSearchResult {
  fileId: string;
  filename: string;
  type: string;
  url: string;
  material?: Material;
}

// General material management
export const getMaterials = async (tagIds?: string[], hasOutline?: boolean): Promise<MaterialWithFileMetadata[]> => {
  const params: any = {};
  if (tagIds && tagIds.length > 0) {
    params.tagIds = tagIds.join(',');
  }
  if (hasOutline) {
    params.hasOutline = true;
  }
  const response = await API().get('/materials', { params });
  return response.data;
};

export const createMaterial = async (formData: FormData): Promise<MaterialWithFileMetadata> => {
  const response = await API().post('/materials', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const uploadFileWithOutline = async (file: File): Promise<MaterialWithFileMetadata> => {
    const formData = new FormData();
    const fileBlob = new Blob([file], { type: file.type });
    formData.append('file', fileBlob, file.name);

    const fileType = getFileType(file);
    formData.append('type', fileType);

    let outline;
    if (fileType === 'docx') {
      outline = await parseDocxOutline(file);
      console.log('Outline:', outline);
      formData.append('outline', JSON.stringify(outline));
    }

    // TODO: handle extracting outline from other file types

    try {
      const material = await createMaterial(formData);
      return material;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }

}

export const getMaterialById = async (materialId: string): Promise<MaterialWithFileMetadata> => {
  const response = await API().get(`/materials/${materialId}`);
  return response.data;
};

export const updateMaterial = async (materialId: string, data: { tagIds?: string[]; projectId?: string | null }): Promise<Material> => {
  const response = await API().put(`/materials/${materialId}`, data);
  return response.data;
};

export const deleteMaterial = async (materialId: string): Promise<void> => {
  await API().delete(`/materials/${materialId}`);
};

export const toggleMaterialEnabled = async (materialId: string): Promise<Material> => {
  const response = await API().post(`/materials/${materialId}/toggle-enabled`);
  return response.data;
};

export const toggleMaterialAutoReferenced = async (materialId: string): Promise<Material> => {
  const response = await API().post(`/materials/${materialId}/toggle-auto-referenced`);
  return response.data;
};

// export const parseMaterialOutline = async (materialId: string): Promise<Material> => {
//   const response = await API().post(`/materials/${materialId}/parse-outline`);
//   return response.data;
// };

export const searchMaterials = async (query: string): Promise<MaterialSearchResult[]> => {
  const response = await API().post('/materials/search', { query });
  return response.data;
};

// Project-specific material management
export const getProjectMaterials = async (projectId: string, tagIds?: string[], hasOutline?: boolean): Promise<MaterialWithFileMetadata[]> => {
  const params: any = {};
  if (tagIds && tagIds.length > 0) {
    params.tagIds = tagIds.join(',');
  }
  if (hasOutline) {
    params.hasOutline = true;
  }
  const response = await API().get(`/projects/${projectId}/materials`, { params });
  return response.data;
};

export const createProjectMaterial = async (projectId: string, formData: FormData): Promise<Material> => {
  const response = await API().post(`/projects/${projectId}/materials`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getProjectMaterialById = async (projectId: string, materialId: string): Promise<MaterialWithFileMetadata> => {
  const response = await API().get(`/projects/${projectId}/materials/${materialId}`);
  return response.data;
};

export const updateProjectMaterial = async (
  projectId: string,
  materialId: string,
  data: { tagIds?: string[] }
): Promise<Material> => {
  const response = await API().put(`/projects/${projectId}/materials/${materialId}`, data);
  return response.data;
};

export const deleteProjectMaterial = async (projectId: string, materialId: string): Promise<void> => {
  await API().delete(`/projects/${projectId}/materials/${materialId}`);
};

export const toggleProjectMaterialEnabled = async (projectId: string, materialId: string): Promise<Material> => {
  const response = await API().post(`/projects/${projectId}/materials/${materialId}/toggle-enabled`);
  return response.data;
};

export const toggleProjectMaterialAutoReferenced = async (projectId: string, materialId: string): Promise<Material> => {
  const response = await API().post(`/projects/${projectId}/materials/${materialId}/toggle-auto-referenced`);
  return response.data;
};

// export const parseProjectMaterialOutline = async (projectId: string, materialId: string): Promise<Material> => {
//   const response = await API().post(`/projects/${projectId}/materials/${materialId}/parse-outline`);
//   return response.data;
// };

export const searchProjectMaterials = async (projectId: string, query: string): Promise<MaterialSearchResult[]> => {
  const response = await API().post(`/projects/${projectId}/materials/search`, { query });
  return response.data;
};

export const associateMaterialsWithProject = async (projectId: string, materialIds: string[]): Promise<Material[]> => {
  const response = await API().post(`/projects/${projectId}/materials/associate`, { materialIds });
  return response.data;
};

export const copyMaterialToLibrary = async (projectId: string, materialId: string): Promise<{ message: string; material?: Material; existing?: boolean }> => {
  const response = await API().post(`/projects/${projectId}/materials/${materialId}/copy-to-library`);
  return response.data;
};
